import React from 'react';
import {
  AccountBalance as AccountBalanceIcon,
  TrendingUp as TrendingUpIcon,
  TrendingDown as TrendingDownIcon,
  Person as PersonIcon,
  LocationCity as LocationCityIcon,
  Router as RouterIcon,
  Settings as SettingsIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  Lock as LockIcon,
  LockOpen as LockOpenIcon
} from '@mui/icons-material';

// Cores para status do caixa
export const getCaixaStatusColor = (status: string) => {
  switch (status) {
    case 'aberto':
      return 'success';
    case 'fechado':
      return 'error';
    default:
      return 'default';
  }
};

// Ícones para status do caixa
export const getCaixaStatusIcon = (status: string) => {
  switch (status) {
    case 'aberto':
      return <CheckCircleIcon color="success" fontSize="small" />;
    case 'fechado':
      return <CancelIcon color="error" fontSize="small" />;
    default:
      return <SettingsIcon fontSize="small" />;
  }
};

// Labels para status do caixa
export const getCaixaStatusLabel = (status: string) => {
  switch (status) {
    case 'aberto':
      return 'Aberto';
    case 'fechado':
      return 'Fechado';
    default:
      return status;
  }
};

// Cores para situação do caixa (ativo/inativo)
export const getCaixaSituacaoColor = (ativo: boolean) => {
  return ativo ? 'success' : 'error';
};

// Ícones para situação do caixa
export const getCaixaSituacaoIcon = (ativo: boolean) => {
  return ativo 
    ? <LockOpenIcon color="success" fontSize="small" />
    : <LockIcon color="error" fontSize="small" />;
};

// Labels para situação do caixa
export const getCaixaSituacaoLabel = (ativo: boolean) => {
  return ativo ? 'Ativo' : 'Inativo';
};

// Ícones para tipo de vinculação
export const getTipoVinculoIcon = (tipo: string) => {
  switch (tipo) {
    case 'cidade':
      return <LocationCityIcon fontSize="small" />;
    case 'usuario':
      return <PersonIcon fontSize="small" />;
    case 'gateway':
      return <RouterIcon fontSize="small" />;
    case 'default':
      return <SettingsIcon fontSize="small" />;
    default:
      return <SettingsIcon fontSize="small" />;
  }
};

// Labels para tipo de vinculação
export const getTipoVinculoLabel = (tipo: string) => {
  switch (tipo) {
    case 'cidade':
      return 'Cidade';
    case 'usuario':
      return 'Usuário';
    case 'gateway':
      return 'Gateway';
    case 'default':
      return 'Default';
    default:
      return tipo;
  }
};

// Cores para tipo de movimentação
export const getMovimentacaoTipoColor = (tipo: string) => {
  return tipo === 'entrada' ? 'success' : 'error';
};

// Ícones para tipo de movimentação
export const getMovimentacaoTipoIcon = (tipo: string) => {
  return tipo === 'entrada' 
    ? <TrendingUpIcon fontSize="small" />
    : <TrendingDownIcon fontSize="small" />;
};

// Labels para tipo de movimentação
export const getMovimentacaoTipoLabel = (tipo: string) => {
  return tipo === 'entrada' ? 'Entrada' : 'Saída';
};

// Labels para formas de pagamento
export const getFormaPagamentoLabel = (forma: string) => {
  const labels: Record<string, string> = {
    'dinheiro': 'Dinheiro',
    'cartao_credito': 'Cartão de Crédito',
    'cartao_debito': 'Cartão de Débito',
    'pix': 'PIX',
    'transferencia': 'Transferência',
    'boleto': 'Boleto',
    'cheque': 'Cheque'
  };
  return labels[forma] || forma;
};

// Labels para tipos de coletor
export const getColetorTipoLabel = (tipo: string) => {
  const labels: Record<string, string> = {
    'usuario': 'Usuário',
    'webhook': 'Webhook / Gateway',
  };
  return labels[tipo] || tipo;
};

// Labels para status de registro
export const getRegistroStatusLabel = (status: string) => {
  const labels: Record<string, string> = {
    'pendente': 'Pendente',
    'confirmado': 'Confirmado',
    'cancelado': 'Cancelado'
  };
  return labels[status] || status;
};

// Cores para status de registro
export const getRegistroStatusColor = (status: string) => {
  switch (status) {
    case 'confirmado':
      return 'success';
    case 'cancelado':
      return 'error';
    case 'pendente':
      return 'warning';
    default:
      return 'default';
  }
};

// Formatação de moeda brasileira
export const formatCurrency = (value: number) => {
  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL'
  }).format(value);
};

// Formatação de data e hora brasileira
export const formatDateTime = (dateString: string, options: {
  dateOnly?: boolean;
  timeOnly?: boolean;
  compact?: boolean;
} = {}) => {
  const date = new Date(dateString);
  
  if (options.dateOnly) {
    return date.toLocaleDateString('pt-BR');
  }
  
  if (options.timeOnly) {
    return date.toLocaleTimeString('pt-BR', { 
      hour: '2-digit', 
      minute: '2-digit' 
    });
  }
  
  if (options.compact) {
    return `${date.toLocaleDateString('pt-BR')} ${date.toLocaleTimeString('pt-BR', { 
      hour: '2-digit', 
      minute: '2-digit' 
    })}`;
  }
  
  return date.toLocaleString('pt-BR');
};

// Validação de saldo
export const validateSaldo = (saldo: string | number): { isValid: boolean; message?: string } => {
  const numericValue = typeof saldo === 'string' ? parseFloat(saldo) : saldo;
  
  if (isNaN(numericValue)) {
    return {
      isValid: false,
      message: 'Valor deve ser um número válido'
    };
  }
  
  if (numericValue < 0) {
    return {
      isValid: false,
      message: 'Valor não pode ser negativo'
    };
  }
  
  return { isValid: true };
};

// Validação de nome do caixa
export const validateNomeCaixa = (nome: string): { isValid: boolean; message?: string } => {
  if (!nome || nome.trim().length === 0) {
    return {
      isValid: false,
      message: 'Nome é obrigatório'
    };
  }
  
  if (nome.trim().length < 3) {
    return {
      isValid: false,
      message: 'Nome deve ter pelo menos 3 caracteres'
    };
  }
  
  if (nome.trim().length > 100) {
    return {
      isValid: false,
      message: 'Nome deve ter no máximo 100 caracteres'
    };
  }
  
  return { isValid: true };
};

// Calcular variação percentual
export const calculatePercentageChange = (valorAtual: number, valorAnterior: number): number => {
  if (valorAnterior === 0) return 0;
  return ((valorAtual - valorAnterior) / Math.abs(valorAnterior)) * 100;
};

// Gerar cores para gráficos
export const getChartColors = (index: number): string => {
  const colors = [
    '#2196F3', // Azul
    '#4CAF50', // Verde
    '#FF9800', // Laranja
    '#F44336', // Vermelho
    '#9C27B0', // Roxo
    '#00BCD4', // Ciano
    '#8BC34A', // Verde claro
    '#FFC107', // Amarelo
    '#E91E63', // Rosa
    '#607D8B'  // Azul acinzentado
  ];
  return colors[index % colors.length];
};

// Obter nome da vinculação
export const getVinculacaoNome = (caixa: {
  cidade?: { nome: string };
  usuario?: { nome: string };
  gateway?: { nome: string };
  tipo_vinculo: string;
}): string => {
  if (caixa.cidade?.nome) return caixa.cidade.nome;
  if (caixa.usuario?.nome) return caixa.usuario.nome;
  if (caixa.gateway?.nome) return caixa.gateway.nome;
  return 'Sistema';
};

// Truncar texto
export const truncateText = (text: string, maxLength: number = 50): string => {
  if (text.length <= maxLength) return text;
  return text.substring(0, maxLength) + '...';
};

// Validar período de datas
export const validateDateRange = (dataInicio?: string, dataFim?: string): { isValid: boolean; message?: string } => {
  if (!dataInicio && !dataFim) {
    return { isValid: true };
  }
  
  if (dataInicio && !dataFim) {
    return {
      isValid: false,
      message: 'Informe também a data fim'
    };
  }
  
  if (!dataInicio && dataFim) {
    return {
      isValid: false,
      message: 'Informe também a data início'
    };
  }
  
  if (dataInicio && dataFim) {
    const inicio = new Date(dataInicio);
    const fim = new Date(dataFim);
    
    if (inicio > fim) {
      return {
        isValid: false,
        message: 'Data início deve ser anterior à data fim'
      };
    }
    
    // Verificar se o período não é muito longo (mais de 1 ano)
    const diffTime = Math.abs(fim.getTime() - inicio.getTime());
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    
    if (diffDays > 365) {
      return {
        isValid: false,
        message: 'Período não pode ser superior a 1 ano'
      };
    }
  }
  
  return { isValid: true };
};
